import { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

// MediaSubmissionForm component
const MediaSubmissionForm = () => {
  // Form state
  const [title, setTitle] = useState("");
  const [presenter, setPresenter] = useState("");
  const [date, setDate] = useState("");
  const [eventType, setEventType] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [description, setDescription] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useRef(null);

  const [eventOptions, setEventOptions] = useState(null);
  const [tagOptions, setTagOptions] = useState(null);
  const [mediaOptions, setMediaOptions] = useState(null);

  // const [selectedTag, setSelectedTag] = useState([]);

  // Sample options - replace with your actual data
  // const eventOptions = [
  //   { name: "Conference", value: "conference" },
  //   { name: "Workshop", value: "workshop" },
  //   { name: "Webinar", value: "webinar" },
  //   { name: "Seminar", value: "seminar" },
  // ];

  // const mediaOptions = [
  //   { name: "Video", value: "video" },
  //   { name: "Audio", value: "audio" },
  //   { name: "PDF", value: "pdf" },
  //   { name: "Image", value: "image" },
  // ];

  // const tagOptions = [
  //   { name: "Technology", value: "technology" },
  //   { name: "Business", value: "business" },
  //   { name: "Design", value: "design" },
  //   { name: "Marketing", value: "marketing" },
  //   { name: "Science", value: "science" },
  //   { name: "Education", value: "education" },
  // ];

  const {
    data: eventsData,
    isLoading: eventsLoading,
    isError: eventsError,
  } = useQuery({
    queryKey: ["events"],
    queryFn: async () => {
      const response = await axios.get("api/resources/events");
      return response.data;
    },
    staleTime: Infinity, // Never goes stale - only loads once
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Effect to handle dropdown population when events data is loaded
  useEffect(() => {
    if (eventsData) {
      const events =
        eventsData.find((item) => item.id === "events")?.names || [];
      const media_types =
        eventsData.find((item) => item.id === "media_type")?.medias || [];
      const tags = eventsData.find((item) => item.id === "tags")?.tags || [];
      const eventOptions = events.map((event) => ({
        name: event,
        value: event,
      }));
      const mediaOptions = media_types.map((media_type) => ({
        name: media_type,
        value: media_type,
      }));

      const tagOptions = tags.map((tag) => ({ name: tag, value: tag }));

      setEventOptions(eventOptions);
      setMediaOptions(mediaOptions);
      setTagOptions(tagOptions);
    }
  }, [eventsData]);

  const pubDates = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { name: year.toString(), value: year };
  });

  // Get tag color based on tag value
  const getTypeColor = (tagValue) => {
    const colorMap = {
      AI: "info",
      "Hit Discovery": "warning",
      "Hit Discovery": "success",
      "DNA ENCODED LIBRARY (DEL)": "warning",
      "Machine Learning": "danger",
      "Drug Discovery": "contrast",
      CACHE: "secondary",
    };

    return colorMap[tagValue.toLowerCase()] || "info";
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.current.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill all required fields",
        life: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    // Create form data object
    const formData = new FormData();
    formData.append("title", title);
    formData.append("presenter", presenter);
    formData.append("date", date);
    formData.append("eventType", eventType?.value || "");
    formData.append("mediaType", mediaType?.value || "");
    formData.append(
      "tags",
      JSON.stringify(selectedTags.map((tag) => tag.value))
    );
    formData.append("description", description);

    try {
      // Replace with your actual API endpoint
      // const response = await fetch('/api/media', {
      //   method: 'POST',
      //   body: formData
      // });

      // if (!response.ok) {
      //   throw new Error('Failed to submit media');
      // }

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Show success message
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Media submitted successfully!",
        life: 3000,
      });

      // Reset form
      resetForm();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Submission Error",
        detail: error.message || "Failed to submit media",
        life: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form validation
  const validateForm = () => {
    return (
      title.trim() !== "" &&
      presenter.trim() !== "" &&
      date !== "" &&
      eventType !== null &&
      mediaType !== null &&
      thumbnailFile !== null
    );
  };

  // Reset form
  const resetForm = () => {
    setTitle("");
    setPresenter("");
    setDate("");
    setEventType(null);
    setMediaType(null);
    setSelectedTags([]);
    setDescription("");
  };

  return (
    <div className="w-full min-h-screen bg-white py-48 md:py-24">
      <div className="container mx-auto px-8 md:px-20  md:flex-row items-start justify-between">
        <Toast ref={toast} />

        {/* Left Side - Form */}
        <div className="w-full max-w-full md:w-full md:flex-shrink-0 border-[#06002e] border-[30px] rounded-xl mb-8 md:pr-4">
          <div className="bg-white p-4 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Submit New Media
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div className="my-8">
                <span className="p-float-label">
                  <InputText
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="custom-input border-2 border-[#06002e] text-black rounded-xl px-2 py-2 transition duration-200 w-full"
                  />
                  <label
                    htmlFor="title"
                    className="font-medium !transition-all !duration-200"
                  >
                    Title *
                  </label>
                </span>
              </div>

              {/* Presenter */}
              <div className="my-8">
                <span className="p-float-label">
                  <InputText
                    id="presenter"
                    value={presenter}
                    onChange={(e) => setPresenter(e.target.value)}
                    className="custom-input border-2 border-[#06002e] text-black rounded-xl px-2 py-2 transition duration-200 w-full"
                  />
                  <label
                    htmlFor="presenter"
                    className="font-medium !transition-all !duration-200"
                  >
                    Presenter *
                  </label>
                </span>
              </div>

              {/* Event Type */}
              <div className="my-4">
                <label className="block text-lg font-medium text-gray-700 mb-1">
                  Event Type *
                </label>
                <Dropdown
                  value={eventType}
                  options={eventOptions}
                  onChange={(e) => setEventType(e.value)}
                  optionLabel="name"
                  placeholder="Select Event"
                  className="text-white text-sm rounded-xl border-2 border-[#06002e] hover:bg-gray hover:text-black transition duration-200 w-full"
                />
              </div>

              {/* Date (Year) */}
              <div className="my-4">
                <label className="block text-lg font-medium text-gray-700 mb-1">
                  Date (Year) *
                </label>
                <Dropdown
                  value={date}
                  options={pubDates}
                  onChange={(e) => setDate(e.value)}
                  optionLabel="name"
                  placeholder="Select Year"
                  className="text-white font-sm rounded-xl border-2 border-[#06002e] hover:bg-gray hover:text-black transition duration-200 w-full"
                />
              </div>

              {/* Media Type */}
              <div className="my-4">
                <label className="block text-lg font-medium text-gray-700 mb-1">
                  Media Type *
                </label>
                <Dropdown
                  value={mediaType}
                  options={mediaOptions}
                  onChange={(e) => setMediaType(e.value)}
                  optionLabel="name"
                  placeholder="Select Media"
                  className="text-white text-sm rounded-xl border-2 border-[#06002e] hover:bg-gray hover:text-black transition duration-200 w-full"
                />
              </div>

              {/* Tags */}
              <div className="my-4">
                <label className="block text-lg font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <MultiSelect
                  value={selectedTags || []}
                  options={tagOptions || []}
                  onChange={(e) => setSelectedTags(e.value || [])}
                  optionLabel="name"
                  placeholder="Select Tags"
                  className="text-black text-sm rounded-xl border-2 border-[#06002e] hover:bg-gray hover:text-black transition duration-200 w-full"
                  display="chip"
                  panelHeaderTemplate={() => (
                    <div className="p-multiselect-header text-center">
                      <span className="font-bold text-center">Select Tags</span>
                    </div>
                  )}
                  selectedItemTemplate={(option) => {
                    if (!option) return null;

                    const displayName =
                      typeof option === "string"
                        ? option
                        : option.name || option.value;
                    return (
                      <div className="flex align-items-left gap-2 px-2 py-1 rounded-lg mr-1 my-1">
                        <span>{displayName}</span>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTags((prev) =>
                              (prev || []).filter((tag) =>
                                typeof tag === "string"
                                  ? tag !== displayName
                                  : tag.value !== option.value
                              )
                            );
                          }}
                          className="flex items-center justify-center w-4 h-4 text-xs rounded-full text-black hover:bg-blue-500"
                        >
                          ×
                        </button>
                      </div>
                    );
                  }}
                />
              </div>

              {/* Description */}
              <div className="my-4">
                <label className="block text-lg font-medium text-gray-700 mb-1">
                  Description
                </label>
                <InputTextarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="text-black rounded-xl px-3 py-2 transition duration-200 w-full border-2 border-[#06002e]"
                />
              </div>

              {/* Thumbnail Upload */}
              <div className="my-8">
                <span className="p-float-label">
                  <InputText
                    id="link"
                    value={presenter}
                    onChange={(e) => setPresenter(e.target.value)}
                    className="custom-input border-2 border-[#06002e] text-black rounded-xl px-2 py-2 transition duration-200 w-full"
                  />
                  <label
                    htmlFor="link"
                    className="font-medium !transition-all !duration-200"
                  >
                    Download Link *
                  </label>
                </span>
              </div>

              {/* Media File Upload */}
              <div className="my-8">
                <span className="p-float-label">
                  <InputText
                    id="publishlink"
                    value={presenter}
                    onChange={(e) => setPresenter(e.target.value)}
                    className="custom-input border-2 border-[#06002e] text-black rounded-xl px-2 py-2 transition duration-200 w-full"
                  />
                  <label
                    htmlFor="publishlink"
                    className="font-medium !transition-all !duration-200"
                  >
                    Publish Link *
                  </label>
                </span>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center mt-8">
                <Button
                  type="submit"
                  label="Submit Media"
                  icon="pi pi-upload"
                  loading={isSubmitting}
                  className="text-[#06002e] font-bold rounded-xl px-6 py-3 border-2 border-[#f36548] hover:bg-[#f36548] hover:text-black transition duration-200"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaSubmissionForm;
